import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { CHART_STATUS_COLORS } from "../../../styles/chartColors";

const AttendanceChart = ({ data }) => {
  const chartRef = useRef();

  useEffect(() => {
    const chartElement = chartRef.current;
    const margin = { top: 20, right: 30, bottom: 80, left: 60 };
    const width = 1600 - margin.left - margin.right;
    const height = 500 - margin.top - margin.bottom;

    d3.select(chartElement).select("svg").remove();

    const svg = d3
      .select(chartElement)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleBand()
      .domain(data.map(d => d.program.name))
      .range([0, width])
      .padding(0.2);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => Math.max(d.present, d.absent, d.leave))])
      .nice()
      .range([height, 0]);

    const subBandWidth = x.bandwidth() / 3; // Divide the bandwidth for three bars

    // Tooltip setup
    const tooltip = d3
      .select(chartElement)
      .append("div")
      .style("position", "fixed")
      .style("z-index", 1000)
      .style("background", "var(--color-dark)")
      .style("color", "var(--color-text-on-dark)")
      .style("padding", "8px 12px")
      .style("border-radius", "8px")
      .style("box-shadow", "0 4px 12px rgba(15, 23, 42, 0.25)")
      .style("font-size", "0.8rem")
      .style("line-height", "1.5")
      .style("visibility", "hidden")
      .style("pointer-events", "none");

    const showTooltip = (event, d) => {
      tooltip
        .html(`Class: ${d.program.name}<br>Present: ${d.present}<br>Absent: ${d.absent}<br>Leave: ${d.leave}`)
        .style("visibility", "visible");

      const tooltipWidth = tooltip.node().offsetWidth;
      const pageWidth = window.innerWidth;
      const mouseX = event.clientX;
      const mouseY = event.clientY;

      const tooltipLeft = mouseX + tooltipWidth + 15 > pageWidth
        ? mouseX - tooltipWidth - 15
        : mouseX + 15;

      tooltip
        .style("top", `${mouseY + 15}px`)
        .style("left", `${tooltipLeft}px`);
    };

    const hideTooltip = () => {
      tooltip.style("visibility", "hidden");
    };

    const barRadius = 3;

    // Draw "present" bars
    svg.append("g")
      .selectAll(".bar-present")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar-present")
      .attr("x", d => x(d.program.name))
      .attr("y", d => y(d.present))
      .attr("width", subBandWidth)
      .attr("height", d => height - y(d.present))
      .attr("rx", barRadius)
      .attr("fill", CHART_STATUS_COLORS.positive)
      .style("cursor", "pointer")
      .on("mouseover", function (event, d) { d3.select(this).style("opacity", 0.8); showTooltip(event, d); })
      .on("mousemove", showTooltip)
      .on("mouseout", function () { d3.select(this).style("opacity", 1); hideTooltip(); });

    // Draw "absent" bars
    svg.append("g")
      .selectAll(".bar-absent")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar-absent")
      .attr("x", d => x(d.program.name) + subBandWidth)
      .attr("y", d => y(d.absent))
      .attr("width", subBandWidth)
      .attr("height", d => height - y(d.absent))
      .attr("rx", barRadius)
      .attr("fill", CHART_STATUS_COLORS.negative)
      .style("cursor", "pointer")
      .on("mouseover", function (event, d) { d3.select(this).style("opacity", 0.8); showTooltip(event, d); })
      .on("mousemove", showTooltip)
      .on("mouseout", function () { d3.select(this).style("opacity", 1); hideTooltip(); });

    // Draw "leave" bars
    svg.append("g")
      .selectAll(".bar-leave")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar-leave")
      .attr("x", d => x(d.program.name) + subBandWidth * 2)
      .attr("y", d => y(d.leave))
      .attr("width", subBandWidth)
      .attr("height", d => height - y(d.leave))
      .attr("rx", barRadius)
      .attr("fill", CHART_STATUS_COLORS.warning)
      .style("cursor", "pointer")
      .on("mouseover", function (event, d) { d3.select(this).style("opacity", 0.8); showTooltip(event, d); })
      .on("mousemove", showTooltip)
      .on("mouseout", function () { d3.select(this).style("opacity", 1); hideTooltip(); });

    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .style("font-size", "0.75rem")
      .style("fill", "var(--color-text-body)")
      .style("cursor", "pointer")
      .on("mouseover", function (event, programName) {
        const d = data.find((item) => item.program.name === programName);
        if (d) showTooltip(event, d);
      })
      .on("mousemove", function (event, programName) {
        const d = data.find((item) => item.program.name === programName);
        if (d) showTooltip(event, d);
      })
      .on("mouseout", hideTooltip);

    svg.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("font-size", "0.75rem")
      .style("fill", "var(--color-text-body)");

    svg.selectAll(".domain, .tick line")
      .style("stroke", "var(--color-border-strong)");

    svg.append("text")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 10)
      .attr("text-anchor", "middle")
      .style("font-size", "0.8rem")
      .style("font-weight", "600")
      .style("fill", "var(--color-text-body)")
      .text("Class");

    svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", -margin.left + 10)
      .attr("text-anchor", "middle")
      .style("font-size", "0.8rem")
      .style("font-weight", "600")
      .style("fill", "var(--color-text-body)")
      .text("Count");

    return () => {
      d3.select(chartElement).select("svg").remove();
      d3.select(chartElement).select("div").remove();
    };
  }, [data]);

  return (
    <div>
      <div className="attendance-chart-legend">
        <span className="attendance-chart-legend-item">
          <span className="attendance-chart-legend-swatch" style={{ backgroundColor: CHART_STATUS_COLORS.positive }}></span>
          Present
        </span>
        <span className="attendance-chart-legend-item">
          <span className="attendance-chart-legend-swatch" style={{ backgroundColor: CHART_STATUS_COLORS.negative }}></span>
          Absent
        </span>
        <span className="attendance-chart-legend-item">
          <span className="attendance-chart-legend-swatch" style={{ backgroundColor: CHART_STATUS_COLORS.warning }}></span>
          Leave
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div ref={chartRef}></div>
      </div>
    </div>
  );
};

export default AttendanceChart;
